import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
// Components
import { Card, Table, SMBSelect } from '../../../../components';
import { Close, DeleteForever, Add, Engineering } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Grid,
  MenuItem,
  Typography,
  Divider,
  TextField,
  ButtonGroup,
  Alert,
  AlertTitle,
  Box
} from '@mui/material';
import { INeighborhoodSupervisors, IDropdownResponse } from '../../../../models';
import {
  getAvailableSupervisors,
  getSupervisorNeighborhoodNames,
  getNeighborhoodSupervisors,
  addSupervisorToNeighborhood,
  deleteSupervisorNeighborhood
} from '../../../../fetch';

export const Supervisors = ({ neighborhoodId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedSupervisorId, setSelectedSupervisorId] = useState<string | number>('');
  const [selectedSupervisorName, setSelectedSupervisorName] = useState<string>('');
  const [supervisorPriorityOrder, setSupervisorPriorityOrder] = useState<string | number>('');
  const [isDeleting, setDeleting] = useState<{ [id: number]: boolean }>({});

  const classes = useStyles();

  const formatDetails = (details: string[]) => {
    let neighborhoods = details.join(', ');

    return (
      <>
        {neighborhoods.length !== 0 ? (
          <Alert severity="info">
            <AlertTitle className={classes.alertTitle}>
              Supervisor {selectedSupervisorName} is currently assigned to:
            </AlertTitle>
            {neighborhoods}
          </Alert>
        ) : (
          <Alert severity="info">
            <AlertTitle className={classes.alertTitle}>
              Supervisor {selectedSupervisorName} is not assigned to any neighborhoods.
            </AlertTitle>
          </Alert>
        )}
      </>
    );
  };

  const {
    isLoading: isLoadingAvailableSupervisors,
    data: availableSupervisors,
    refetch: refetchAvailableSupervisors
  } = useQuery<IDropdownResponse[], Error>(
    ['getAvailableSupervisors'],
    () => getAvailableSupervisors(),
    {
      notifyOnChangeProps: 'tracked'
    }
  );

  const { isLoading: isLoadingSupervisorNeighborhoodNames, data: supervisorNeighborhoodNames } =
    useQuery<string[], Error>(
      ['getSupervisorNeighborhoodNames', selectedSupervisorId],
      () => getSupervisorNeighborhoodNames(selectedSupervisorId),
      {
        notifyOnChangeProps: 'tracked',
        enabled: !!selectedSupervisorId
      }
    );

  const {
    isLoading: isLoadingNeighborhoodSupervisors,
    data: neighborhoodSupervisors,
    refetch: refetchNeighborhoodSupervisors
  } = useQuery<INeighborhoodSupervisors[], Error>(
    ['getNeighborhoodSupervisors', neighborhoodId],
    () => getNeighborhoodSupervisors(neighborhoodId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!neighborhoodId
    }
  );

  const handleDelete = async (supervisorId: number | string) => {
    const result = window.confirm('Are you sure you want to delete this supervisor?');
    if (result) {
      setDeleting({ [neighborhoodId]: true });
      try {
        await deleteSupervisorNeighborhood(supervisorId, neighborhoodId);
        refetchNeighborhoodSupervisors();
        enqueueSnackbar('Supervisor removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Error deleting supervisor.', {
          variant: 'error'
        });
      } finally {
        setDeleting({ [neighborhoodId]: false });
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Priority',
        accessor: 'priorityOrder',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INeighborhoodSupervisors } };
        }) => {
          return (
            <>
              <LoadingButton
                loading={isDeleting[original.supervisorId]}
                loadingPosition="start"
                color="error"
                disabled={isDeleting[original.supervisorId]}
                startIcon={<DeleteForever />}
                onClick={() => {
                  handleDelete(original.supervisorId);
                }}
              >
                Delete
              </LoadingButton>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card title="Supervisors" icon={<Engineering />}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <SMBSelect
            variant="outlined"
            disabled={isLoadingAvailableSupervisors}
            name={`neighborhoodId`}
            label={`Supervisor`}
            id={`neighborhoodId`}
            value={selectedSupervisorId}
            size="small"
            handleClear={() => setSelectedSupervisorId('')}
            onChange={e => {
              setSelectedSupervisorId(e.target.value);
            }}
          >
            {!isLoadingAvailableSupervisors && availableSupervisors?.length === 0 && (
              <MenuItem disabled>No neighborhoods available</MenuItem>
            )}
            {!isLoadingAvailableSupervisors &&
              availableSupervisors?.map((record, index) => {
                return (
                  <MenuItem
                    key={`${index}`}
                    value={record.value}
                    onClick={() => {
                      setSelectedSupervisorName(record.description);
                    }}
                  >
                    {record.description}
                  </MenuItem>
                );
              })}
          </SMBSelect>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            type="number"
            fullWidth
            size="small"
            autoComplete="nope"
            label="Priority Order"
            name="priorityOrder"
            value={supervisorPriorityOrder}
            onChange={e => {
              setSupervisorPriorityOrder(e.target.value);
            }}
          />
        </Grid>
      </Grid>
      <ButtonGroup>
        <Box mb={1} mt={1}>
          <Button
            className={classes.button}
            startIcon={<Add />}
            sx={{ minWidth: '14rem' }}
            color="primary"
            variant="contained"
            disabled={!selectedSupervisorId || !supervisorPriorityOrder}
            onClick={async () => {
              try {
                await addSupervisorToNeighborhood(
                  neighborhoodId,
                  selectedSupervisorId,
                  supervisorPriorityOrder
                );
                refetchAvailableSupervisors();
                refetchNeighborhoodSupervisors();
                setSelectedSupervisorId('');
                setSupervisorPriorityOrder('');
                enqueueSnackbar('Supervisor added!', {
                  variant: 'success'
                });
              } catch (error) {
                enqueueSnackbar(error?.Detail ?? 'Error adding Supervisor.', {
                  variant: 'error'
                });
              }
            }}
          >
            Add
          </Button>
          <Button
            className={classes.button}
            startIcon={<Close />}
            sx={{ minWidth: '14rem' }}
            color="inherit"
            variant="contained"
            disabled={!selectedSupervisorId}
            onClick={() => {
              setSelectedSupervisorId('');
              setSupervisorPriorityOrder('');
            }}
          >
            Cancel
          </Button>
        </Box>
      </ButtonGroup>
      {supervisorNeighborhoodNames && !isLoadingSupervisorNeighborhoodNames && (
        <Typography className={classes.roleDetails}>
          {formatDetails(supervisorNeighborhoodNames)}
        </Typography>
      )}
      <Divider />
      <Grid item xs={12}>
        <Table
          columns={columns}
          data={neighborhoodSupervisors ?? []}
          hidePagination
          isLoading={isLoadingNeighborhoodSupervisors}
        />
      </Grid>
    </Card>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  button: {
    whiteSpace: 'nowrap'
  },
  buttonGroup: {
    marginTop: theme.spacing(1)
  },
  roleDetails: {
    paddingBottom: theme.spacing(1)
  },
  selectWrapper: {
    width: '50%',
    marginBottom: '12px',
    '@media (max-width: 768px)': {
      width: '100%'
    }
  },
  textfieldWrapper: {
    width: '50%',
    '@media (max-width: 768px)': {
      width: '100%'
    }
  },
  alertTitle: {
    fontWeight: 'bold'
  }
}));
